import { useEffect, useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { Send, Square } from 'lucide-react'
import { cn } from '@/lib/cn'

/** The question box. Enter sends, Shift+Enter is a new line; Stop ends a running answer. */
export default function ChatComposer({
  value,
  onChange,
  onSend,
  onStop,
  busy,
}: {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onStop: () => void
  busy: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)

  // Grows with the text up to about six lines, then scrolls.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }, [value])

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      if (!busy && value.trim()) onSend()
    }
  }

  return (
    <div className="mt-3 flex items-end gap-2 rounded-xl border border-foreground/15 bg-background p-2 focus-within:border-primary/40">
      <textarea
        ref={ref}
        rows={1}
        value={value}
        maxLength={4000}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Ask about production days, section due dates, late jobs..."
        // 16px below sm, or iOS zooms on focus.
        className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent px-2 py-2 text-base outline-none placeholder:text-foreground/40 sm:text-sm"
      />
      <button
        type="button"
        onClick={busy ? onStop : onSend}
        disabled={!busy && !value.trim()}
        aria-label={busy ? 'Stop' : 'Send'}
        className={cn(
          'inline-flex size-10 shrink-0 items-center justify-center rounded-lg border transition-colors disabled:opacity-40',
          busy
            ? 'border-danger/60 bg-danger/20 text-danger hover:bg-danger/5'
            : 'border-primary/60 bg-primary/20 text-primary hover:bg-primary/5',
        )}
      >
        {busy ? <Square className="size-4" /> : <Send className="size-4" />}
      </button>
    </div>
  )
}
